import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateEntityMutation, useUpdateEntityMutation } from '../../../../hooks/mutations/useEntityMutation';
import { useGeocodeQuery } from '../../../../hooks/queries/useGeocodingQuery';
import { Entity } from '../../../../services/apiSLTP/sites/entities';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';
import { MuiTelInput } from 'mui-tel-input';

interface ModalProps {
    entity: Entity | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateEntityModal: React.FC<ModalProps> = ({ entity, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(entity?.designation || '');
    const [address, setAddress] = useState(entity?.address || '');
    const [debouncedAddress, setDebouncedAddress] = useState(entity?.address || '');
    const [latitude, setLatitude] = useState(entity?.latitude?.toString() ?? '');
    const [longitude, setLongitude] = useState(entity?.longitude?.toString() ?? '');
    const [status, setStatus] = useState(entity?.crudStatus || crudStatusEnum.OK);
    const [phoneNumber, setPhoneNumber] = useState(entity?.phoneNumber || '');

    const { data: coords, isLoading: isGeocoding } = useGeocodeQuery({
        address: debouncedAddress,
    });

    const { mutate: mutateEntity } = useUpdateEntityMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating entity:', error.message),
    });

    const { mutate: addEntity } = useCreateEntityMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating entity:', error.message),
    });

    const handleOk = () => {
        const payload = {
            designation,
            crudStatus: status,
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
            address,
            phoneNumber,
        };
        isEditMode ? mutateEntity({ entityId: entity?._id || '', data: payload }) : addEntity(payload);
    };

    const debouncedSetAddress = useCallback(
        debounce((value: string) => {
            setDebouncedAddress(value);
        }, 500),
        []
    );

    useEffect(() => {
        if (coords) {
            setLatitude(coords.lat.toString());
            setLongitude(coords.lon.toString());
        }
    }, [coords]);

    useEffect(() => {
        debouncedSetAddress(address);
    }, [address, debouncedSetAddress]);

    return (
        <ModalBody
            title={t('entity')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation.length || !address.length}
            onSubmit={handleOk}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} errorText={t('error')} required />
            <ToggleLine
                checked={status === crudStatusEnum.OK}
                onChange={(isActive) => setStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={address} label={t('address')} onChange={setAddress} errorText={t('error')} required />
            <CustomTextInput value={latitude} label={t('latitude')} onChange={(text) => setLatitude(text)} type="number" />
            <CustomTextInput value={longitude} label={t('longitude')} onChange={(text) => setLongitude(text)} type="number" />
            <div className="pad-l-5px pad-r-5px">
                <MuiTelInput
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    label={capitalizeFirstLetter(t('mobile'))}
                    defaultCountry="FR"
                    preferredCountries={['FR', 'BE', 'CH', 'LU']}
                    fullWidth
                    size="small"
                    langOfCountryName={t('locale')}
                />
            </div>
        </ModalBody>
    );
};

export default UpdateEntityModal;
